import { Alert, Box, Button, Grid, Link, Stack, Typography } from '@mui/material';
import type { GridSize } from '@mui/material/Grid';
import { trim } from 'es-toolkit/compat';
import React from 'react';

import CopyButton from 'components/atoms/Buttons/CopyButton/CopyButton';
import { useClipboard } from 'use-clipboard-copy';
import { CliCommandBlock } from './CopyableCliBlock';
import { FieldContainer, FieldText, Label } from './UploadSlidesFormUI';

interface TemporaryCredentials {
  AccessKeyId: string;
  SecretAccessKey: string;
}

interface TemporaryAwsCredentialsStepProps {
  awsProfileName: string;
  credentials: TemporaryCredentials;
  isError: boolean;
  isGenerating: boolean;
  isLoading: boolean;
  onGenerate: () => void;
  onExport: () => void;
  region: string;
}

const TemporaryAwsCredentialsStep = ({
  awsProfileName: rawAwsProfileName,
  credentials,
  isError,
  isGenerating,
  isLoading,
  onGenerate,
  onExport,
  region,
}: TemporaryAwsCredentialsStepProps) => {
  const awsProfileName = trim(rawAwsProfileName);
  const clipboard = useClipboard();
  const hasCredentials = Boolean(credentials.AccessKeyId && credentials.SecretAccessKey);

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Generate temporary Nucleai AWS credentials, then configure AWS CLI with a dedicated upload profile.
      </Typography>
      <Button
        variant="contained"
        disabled={isGenerating || isLoading}
        onClick={onGenerate}
        sx={{ alignSelf: 'flex-start' }}
      >
        {isGenerating ? 'Generating...' : isLoading ? 'Loading lab settings...' : 'Generate Credentials'}
      </Button>
      {isError ? <Alert severity="error">Failed to generate credentials.</Alert> : null}
      {hasCredentials ? (
        <>
          <Grid container spacing={1.5}>
            <CredentialField
              label="Access Key"
              value={credentials.AccessKeyId}
              onCopy={() => clipboard.copy(credentials.AccessKeyId)}
              gridSize={{ xs: 12, sm: 6, md: 3 }}
            />
            <CredentialField
              label="Secret Key"
              value={credentials.SecretAccessKey}
              onCopy={() => clipboard.copy(credentials.SecretAccessKey)}
              gridSize={{ xs: 12, sm: 6, md: 5 }}
            />
            <CredentialField
              label="Default region name"
              value={region}
              onCopy={() => clipboard.copy(region)}
              gridSize={{ xs: 12, sm: 6, md: 2 }}
            />
            <CredentialField label="Default output format" value="Leave empty" gridSize={{ xs: 12, sm: 6, md: 2 }} />
          </Grid>
          <Alert severity="warning">
            Credentials expire in 90 days. Store them securely.{' '}
            <Link color="primary" onClick={onExport} underline="none" sx={{ cursor: 'pointer' }}>
              Download as Excel
            </Link>
          </Alert>
        </>
      ) : null}
      <Typography variant="body2" color="text.secondary">
        Run this command in the same terminal window to save the temporary credentials under your upload profile.
      </Typography>
      <CliCommandBlock command={awsProfileName ? `aws configure --profile ${awsProfileName}` : 'aws configure'} />
      <Typography variant="body2" color="text.secondary">
        After configuring the profile, verify the temporary credentials before uploading.
      </Typography>
      <CliCommandBlock
        command={
          awsProfileName ? `aws sts get-caller-identity --profile ${awsProfileName}` : 'aws sts get-caller-identity'
        }
      />
      <Typography variant="body2" color="text.secondary">
        If this command fails, share the exact output with IT or Nucleai support.
      </Typography>
    </Stack>
  );
};

const CredentialField = ({
  gridSize,
  label,
  onCopy,
  value,
}: {
  gridSize: { xs: GridSize; sm: GridSize; md: GridSize };
  label: string;
  onCopy?: () => void;
  value: string;
}) => (
  <Grid item {...gridSize}>
    <FieldContainer>
      <Label variant="body2">{label}</Label>
      <Box display="flex" alignItems="center" gap="5px" width="100%" minWidth={0}>
        <FieldText>{value}</FieldText>
        {Boolean(onCopy) && (
          <Box flexShrink={0}>
            <CopyButton onCopy={onCopy!} />
          </Box>
        )}
      </Box>
    </FieldContainer>
  </Grid>
);

export default TemporaryAwsCredentialsStep;
